import { useEffect, useRef, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import {
  createAssetHoldingLocation,
  type AssetHoldingLocation,
} from '@/modules/inventory/holdings/locationsService'

export interface AssetHoldingLocationFormModalMessages {
  title: string
  submitTitle: string
  nameLabel: string
  namePlaceholder: string
  nameRequired: string
  descriptionLabel: string
  descriptionPlaceholder: string
  error: string
  closeLabel: string
}

interface AssetHoldingLocationFormModalProps {
  unitId: string
  isOpen: boolean
  onClose: () => void
  onCreated: (location: AssetHoldingLocation) => void
  messages: AssetHoldingLocationFormModalMessages
}

const INPUT_CLS =
  'w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50'

export function AssetHoldingLocationFormModal({
  unitId,
  isOpen,
  onClose,
  onCreated,
  messages,
}: AssetHoldingLocationFormModalProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!isOpen) return

    setName('')
    setDescription('')
    setError(null)
    setTimeout(() => nameRef.current?.focus(), 80)
  }, [isOpen])

  async function handleSave() {
    const trimmedName = name.trim()

    if (!trimmedName) {
      setError(messages.nameRequired)
      nameRef.current?.focus()
      return
    }

    setSaving(true)
    setError(null)

    try {
      const created = await createAssetHoldingLocation(unitId, {
        name: trimmedName,
        description: description.trim() || null,
      })
      onCreated(created)
    } catch (err) {
      setError(err instanceof Error ? err.message : messages.error)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={messages.title}
      size="sm"
      headerRight={
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex items-center gap-2 text-emerald-600 dark:text-emerald-300 hover:text-emerald-800 dark:hover:text-emerald-100 disabled:opacity-40 transition-colors"
            aria-label={messages.submitTitle}
            title={messages.submitTitle}
          >
            {saving ? (
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-white transition-colors disabled:opacity-40"
            aria-label={messages.closeLabel}
            title={messages.closeLabel}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {messages.nameLabel} <span className="text-red-500">*</span>
          </label>
          <input
            ref={nameRef}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && !saving && name.trim() && handleSave()}
            placeholder={messages.namePlaceholder}
            className={INPUT_CLS}
            disabled={saving}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            {messages.descriptionLabel}
          </label>
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={messages.descriptionPlaceholder}
            className={`${INPUT_CLS} min-h-24 resize-y`}
            disabled={saving}
          />
        </div>

        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </Modal>
  )
}
